import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useConfirm } from 'material-ui-confirm';

import { restoreSystemUser } from '@/api/accounts';

interface RestoreSystemUserCardProps {
  accountId: string;
  userId: string;
  userName: string;
  onSuccess: () => void;
}

export function RestoreSystemUserCard({
  accountId,
  userId,
  userName,
  onSuccess,
}: RestoreSystemUserCardProps): React.JSX.Element {
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const restoreMutation = useMutation({
    mutationFn: () => restoreSystemUser(accountId, userId),
    onSuccess: () => {
      toast.success('User restored successfully');
      queryClient.invalidateQueries({ queryKey: ['system-users'] });
      queryClient.invalidateQueries({ queryKey: ['system-user', userId] });
      onSuccess();
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message || error?.message || 'Failed to restore user';
      toast.error(message);
    },
  });

  const handleRestore = async () => {
    try {
      await confirm({
        title: 'Restore User',
        description: `Restore ${userName}? They will reappear in active system user lists and can be edited again.`,
        confirmationButtonProps: { color: 'success' },
        confirmationText: 'Restore',
        cancellationText: 'Cancel',
      });
      restoreMutation.mutate();
    } catch {
      // User cancelled
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
    >
      <Box
        sx={{
          p: { xs: 2, md: 3 },
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Restore User
        </Typography>
      </Box>
      <Box sx={{ p: 2 }}>
        <Stack spacing={1}>
          <Typography color="text.secondary" variant="body2">
            Restore this user to make them active again. You can then edit their details,
            communities, and units.
          </Typography>
          <Button
            color="success"
            disabled={restoreMutation.isPending}
            onClick={handleRestore}
            variant="contained"
          >
            {restoreMutation.isPending ? 'Restoring...' : 'Restore User'}
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
}
